/**
 * @fileoverview The Pending Review Summary tiles above the Pending Approval table.
 * @description Four counters from GET /approvals/summary: requests waiting, how many came from
 * Managers and from Executives, and how many are hot leads. They count every pending request in the
 * organisation, not only the viewer's, and only a Super Admin or Sales Head may read them, so the
 * query stays idle (and nothing renders) unless `enabled`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useApprovalSummary } from '@/features/sales/hooks/useApprovalSummary'
import KpiStrip from '@/shared/components/molecules/KpiStrip'

/**
 * @component
 * @param {{ enabled: boolean }} props - Whether the viewer may read the summary.
 * @param {boolean} props.enabled - Whether to fetch and show it.
 */
function PendingApprovalsSummary({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation()
  const summaryQuery = useApprovalSummary(enabled)

  if (!enabled || !summaryQuery.isSuccess) return null
  const summary = summaryQuery.data

  return (
    <div className="px-3.5 pb-3.5 sm:px-4" data-testid="pending-approvals-summary">
      <KpiStrip
        tiles={[
          {
            label: t('sales.pipeline.pending.summary.waiting'),
            value: summary.waiting,
            testId: 'pending-approvals-summary-waiting',
            tone: summary.waiting > 0 ? 'warning' : undefined,
            icon: 'hourglass',
            iconTone: 'amber',
          },
          {
            label: t('sales.pipeline.pending.summary.fromManagers'),
            value: summary.fromManagers,
            icon: 'users',
            iconTone: 'navy',
            testId: 'pending-approvals-summary-from-managers',
          },
          {
            label: t('sales.pipeline.pending.summary.fromExecutives'),
            value: summary.fromExecutives,
            icon: 'userCheck',
            iconTone: 'blue',
            testId: 'pending-approvals-summary-from-executives',
          },
          {
            label: t('sales.pipeline.pending.summary.hotLeads'),
            value: summary.hotLeads,
            icon: 'alert',
            iconTone: 'red',
            testId: 'pending-approvals-summary-hot-leads',
          },
        ]}
      />
    </div>
  )
}

export default PendingApprovalsSummary
